import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Card, 
  CardContent, 
  CircularProgress,
  Paper,
  Chip,
  Divider,
  IconButton,
  useTheme,
  alpha
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { topMockInterviews } from '../../components/top_job/top_job_const';
import { TopBar } from '../../components/top_bar/TopBar';
import { Helmet } from 'react-helmet';

const InterviewDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [interview, setInterview] = useState(null);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const TITLE = 'Top Interviews - Mirwork';

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      // Find interview based on slusg
      const slugParts = slug.split("-");
      const interviewId = parseInt(slugParts[0], 10);
      
      const matchedInterview = topMockInterviews.find(
        (item) => item.id === interviewId
      );
      
      setInterview(matchedInterview || null);
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [slug]);

  if (loading) {
    return (
      <Box 
        sx={{ 
          minHeight: "100vh", 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center" 
        }}
      >
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  if (!interview) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 8, textAlign: "center" }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Interview Not Found
          </Typography>
          <Typography variant="body1" paragraph>
            We couldn't find the interview you're looking for.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/")}
            startIcon={<ArrowBackIcon />}
          >
            Back to Interviews
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <>
    <Helmet>
        <title>{TITLE}</title>
    </Helmet>
    <TopBar />
    <Box 
      sx={{ 
        minHeight: "100vh", 
        bgcolor: "background.default",
        pb: 8,
        backgroundImage: `linear-gradient(120deg, ${alpha(theme.palette.primary.light, 0.05)}, ${alpha(theme.palette.secondary.light, 0.05)})`,
      }}
    >
      <Box 
        component="header" 
        sx={{ 
          py: { xs: 5, md: 8 }, 
          mb: 6,
          position: "relative",
          backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          color: 'white',
          borderRadius: { xs: 0, md: "0 0 30px 30px" },
          boxShadow: 3
        }}
      >
        <Container maxWidth="md">
          <IconButton 
            onClick={() => navigate("/topInterview")} 
            sx={{ 
              color: "white", 
              mb: 2,
              bgcolor: alpha('#nnn', 0.1),
              "&:hover": {
                bgcolor: alpha('#nnn', 0.2)
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <Paper
              elevation={6}
              sx={{
                p: 2,
                mr: 3,
                bgcolor: '#nnn',
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 60,
                height: 60,
                borderRadius: 2
              }}
            >
              <img
                src={interview.logo}
                alt={`${interview.companyName} logo`}
                style={{ maxWidth: "100%", maxHeight: "100%" }}
              />
            </Paper>
            
            <Box>
              <Typography variant="h3" component="h1" sx={{ fontWeight: 700 }}>
                {interview.companyName}
              </Typography>
              <Typography variant="h5" sx={{ opacity: 0.9 }}>
                {interview.jobName} Interview
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
            {interview.difficulty && (
              <Chip 
                label={interview.difficulty} 
                color={
                  interview.difficulty === "Easy" ? "success" :
                  interview.difficulty === "Medium" ? "warning" : "error"
                }
                sx={{ bgcolor: alpha("#fff", 0.15), fontWeight: 500 }}
              />
            )}
            {interview.duration && (
              <Chip 
                label={interview.duration} 
                color="info"
                sx={{ bgcolor: alpha("#fff", 0.15), fontWeight: 500 }}
              />
            )}
            {interview.questionCount && (
              <Chip 
                label={`${interview.questionCount} Questions`} 
                sx={{ bgcolor: alpha("#fff", 0.15), fontWeight: 500 }}
              />
            )}
          </Box>
        </Container>
      </Box>
      
      <Container maxWidth="md">
        <Card elevation={3} sx={{ mb: 4, borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              About This Mock Interview
            </Typography>
            <Typography variant="body1" paragraph>
              {interview.description}
            </Typography>
            <Typography variant="body1" paragraph>
              This mock interview will help you prepare for real interviews at {interview.companyName} for the {interview.jobName} position. 
              You'll face questions similar to those asked in actual interviews, allowing you to practice your responses and get comfortable 
              with the interview format.
            </Typography>
          </CardContent>
        </Card>
        
        <Box sx={{ textAlign: "center", my: 6 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            sx={{
              py: 2,
              px: 6,
              fontSize: "1.2rem",
              borderRadius: 3,
              boxShadow: 4,
              "&:hover": {
                transform: "scale(1.05)",
                transition: "transform 0.2s"
              }
            }}
            onClick={() => window.location.href = interview.interviewLink}
          >
            Start Mock Interview
          </Button>
        </Box>
      </Container>
    </Box>
    </>
  );
};

export default InterviewDetail;